import { useRef, useState } from 'react';

const brands = [
  { name: 'BMW', logo: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/marcas-1_03.png' },
  { name: 'Mercedes-Benz', logo: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/marcas-1_17.png' },
  { name: 'Audi', logo: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/marcas-1_02.png' },
  { name: 'Land Rover', logo: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/marcas-1_12.png' },
  { name: 'Porsche', logo: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/marcas-1_13.png' },
  { name: 'Volvo', logo: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/marcas-1_25.png' },
  { name: 'Mini', logo: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/marcas-1_01.png' },
  { name: 'Jaguar', logo: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/marcas-1_26.png' },
];

const galleryImages = [
  { src: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/2023-07-17-6-2.jpg', alt: 'Oficina Miki Motors' },
  { src: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/2023-07-17-4-2.jpg', alt: 'Serviço em importado' },
  { src: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/2023-07-17-3-2.jpg', alt: 'Mecânica especializada' },
  { src: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/2023-07-17-5-2.jpg', alt: 'Diagnóstico computadorizado' },
  { src: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/2023-07-17-2.jpg', alt: 'Equipe qualificada' },
];

// Duplica as imagens para criar o efeito de loop infinito
const loopImages = [...galleryImages, ...galleryImages, ...galleryImages];

const CARD_WIDTH = 380;
const CARD_GAP = 16;
const CARD_STEP = CARD_WIDTH + CARD_GAP;
const TOTAL_ORIGINAL = galleryImages.length;

function GalleryCarousel() {
  const [offset, setOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const animRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number | null>(null);
  const speedRef = useRef(0.09); // px por ms
  const pausedRef = useRef(false);
  const offsetRef = useRef(0);

  // Animação contínua via requestAnimationFrame
  const animate = (time: number) => {
    if (lastTimeRef.current !== null && !pausedRef.current) {
      const delta = time - lastTimeRef.current;
      offsetRef.current += speedRef.current * delta;
      // Loop: quando passa o bloco original, volta ao início
      const loopWidth = TOTAL_ORIGINAL * CARD_STEP;
      if (offsetRef.current >= loopWidth) {
        offsetRef.current -= loopWidth;
      }
      setOffset(offsetRef.current);
    }
    lastTimeRef.current = time;
    animRef.current = requestAnimationFrame(animate);
  };

  // Inicia animação ao montar
  const startAnim = () => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    lastTimeRef.current = null;
    animRef.current = requestAnimationFrame(animate);
  };

  // Usa useRef para iniciar apenas uma vez
  const startedRef = useRef(false);
  if (!startedRef.current) {
    startedRef.current = true;
    // Inicia no próximo tick
    setTimeout(startAnim, 0);
  }

  const handleMouseEnter = () => { pausedRef.current = true; };
  const handleMouseLeave = () => {
    if (!isDragging) pausedRef.current = false;
  };

  // Drag para arrastar manualmente
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.clientX);
    setDragOffset(0);
    pausedRef.current = true;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const diff = e.clientX - startX;
    setDragOffset(diff);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    offsetRef.current = Math.max(0, offsetRef.current - dragOffset);
    setDragOffset(0);
    setIsDragging(false);
    pausedRef.current = false;
  };

  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });

  const handleImgMouseMove = (e: React.MouseEvent<HTMLDivElement>, idx: number) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHoverIdx(idx);
    setMousePos({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    });
  };

  const totalOffset = offset - dragOffset;

  return (
    <div
      className="relative overflow-hidden select-none"
      style={{ height: 320 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* Fade edges */}
      <div className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none" style={{ background: 'linear-gradient(to right, #0d0d0d, transparent)' }} />
      <div className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none" style={{ background: 'linear-gradient(to left, #0d0d0d, transparent)' }} />

      <div
        className="flex absolute top-0 left-0"
        style={{
          gap: CARD_GAP,
          transform: `translateX(${-totalOffset}px)`,
          willChange: 'transform',
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
      >
        {loopImages.map((img, idx) => (
          <div
            key={idx}
            className="relative overflow-hidden rounded-2xl flex-shrink-0 group"
            style={{ width: CARD_WIDTH, height: 300 }}
            onMouseMove={(e) => handleImgMouseMove(e, idx)}
            onMouseLeave={() => setHoverIdx(null)}
          >
            <img
              src={img.src}
              alt={img.alt}
              className="w-full h-full object-cover object-top"
              style={{
                transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transform: hoverIdx === idx
                  ? `scale(1.08) translate(${(mousePos.x - 0.5) * -8}px, ${(mousePos.y - 0.5) * -8}px)`
                  : 'scale(1)',
              }}
              draggable={false}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
              <span className="text-white text-sm font-semibold tracking-wide">{img.alt}</span>
            </div>
            {hoverIdx === idx && (
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: `radial-gradient(circle at ${mousePos.x * 100}% ${mousePos.y * 100}%, rgba(255,255,255,0.08) 0%, transparent 60%)` }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Vehicles() {
  return (
    <section id="veiculos" className="relative py-24 overflow-hidden" style={{ background: 'linear-gradient(145deg, #0d0d0d 0%, #131313 35%, #111111 65%, #0f0f0f 100%)' }}>
      {/* Premium decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C0C0C0]/20 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C0C0C0]/10 to-transparent"></div>
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full" style={{ background: 'radial-gradient(ellipse, rgba(192,192,192,0.04) 0%, transparent 70%)' }}></div>
        <div className="absolute -bottom-10 -left-20 w-72 h-72 rounded-full" style={{ background: 'radial-gradient(circle, rgba(160,140,100,0.04) 0%, transparent 70%)' }}></div>
        <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-transparent via-[#C0C0C0]/8 to-transparent"></div>
      </div>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#C0C0C0] text-sm font-semibold tracking-widest uppercase mb-4">
            Marcas que Atendemos
          </span>
          <h2
            className="text-4xl md:text-5xl font-black text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Trabalhamos com as{' '}
            <span className="text-[#D4D4D4]">Principais Marcas</span>
            <br />do Mercado
          </h2>
          <p className="text-white/50 text-base max-w-xl mx-auto">
            Profissionais certificados para cada marca, com equipamentos de diagnóstico de última geração.
          </p>
        </div>

        {/* Brands grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mb-20">
          {brands.map((brand) => (
            <div
              key={brand.name}
              className="group flex flex-col items-center justify-center gap-4 bg-[#141414] border border-white/5 rounded-2xl p-8 h-56 hover:border-[#C0C0C0]/40 hover:bg-[#1a1a1a] transition-all duration-300 cursor-default"
            >
              <img
                src={brand.logo}
                alt={brand.name}
                className="h-32 w-auto max-w-[200px] object-contain filter brightness-75 group-hover:brightness-100 transition-all duration-300"
              />
              <span className="text-white/40 group-hover:text-white/70 text-xs font-medium tracking-widest uppercase transition-colors duration-300">
                {brand.name}
              </span>
            </div>
          ))}
        </div>

        {/* Gallery Carousel */}
        <GalleryCarousel />

        {/* CTA */}
        <div className="text-center mt-12">
          <a
            href="https://api.whatsapp.com/send/?phone=5519997889998&text=Olá, gostaria de agendar um serviço&type=phone_number&app_absent=0"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 border border-[#C0C0C0] text-[#C0C0C0] font-semibold px-8 py-4 rounded-full hover:bg-[#C0C0C0] hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            Fale com um Especialista
          </a>
        </div>
      </div>
    </section>
  );
}
